/**
 * Normalize a user-typed org outcome slug for persist.
 * Preserves letter case and underscores; spaces become hyphens.
 * Strips characters Galactus OutcomeKey would reject (quotes, controls, etc.).
 */
export function slugifyOrgOutcomeKey(raw: string): string {
  return raw
    .trim()
    .replace(/\s+/g, "-")
    .replace(/[^A-Za-z0-9_-]/g, "")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");
}

/**
 * Case- and separator-insensitive fold for reserved/duplicate checks.
 * Spaces, hyphens, and underscores collapse to a single hyphen.
 */
export function foldOutcomeKeyForCompare(raw: string): string {
  return raw
    .trim()
    .toLowerCase()
    .replace(/[\s_-]+/g, "-")
    .replace(/^-|-$/g, "");
}

/** True when `raw` is already a persistable key (non-empty and unchanged by slugify). */
export function isNormalizedOrgOutcomeKey(raw: string): boolean {
  return raw.length > 0 && slugifyOrgOutcomeKey(raw) === raw;
}

export type OutcomeKeyCollision = {
  kind: "match" | "near";
  existingKey: string;
};

/**
 * Compare a typed candidate to existing keys.
 * `match`: same persistable key ignoring case (e.g. "short call" / "Busy" vs "short-call" / "busy").
 * `near`: same folded identity but a different persistable key (e.g. "short_call" vs "short-call").
 */
export function classifyOutcomeKeyCollision(
  raw: string,
  existingKeys: Iterable<string>
): OutcomeKeyCollision | null {
  const folded = foldOutcomeKeyForCompare(raw);
  if (!folded) return null;

  const slug = slugifyOrgOutcomeKey(raw);
  let near: OutcomeKeyCollision | null = null;

  for (const existing of existingKeys) {
    const existingSlug = slugifyOrgOutcomeKey(existing);
    if (
      slug.length > 0 &&
      existingSlug.length > 0 &&
      slug.toLowerCase() === existingSlug.toLowerCase()
    ) {
      return { kind: "match", existingKey: existing };
    }
    if (!near && foldOutcomeKeyForCompare(existing) === folded) {
      near = { kind: "near", existingKey: existing };
    }
  }

  return near;
}
